import {
  Box,
  Dialog,
  Button,
  Checkbox,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  CircularProgress
} from '@mui/material';

import { CustomDatePicker } from '../../../../components';

import { SaveConfirmDialogProps } from './SaveConfirmDialog.types';
import './SaveConfirmDialog.css';

/**
 * Save confirmation dialog component for price processing
 * Displays different options based on user role and OKKO changes
 * Allows configuration of order creation and notification settings
 *
 * Features:
 * - Role-based dialog content (User vs Admin)
 * - Order creation checkbox with auto-enable of notifications
 * - SMS, Email, 1C, and OKKO Core notification options
 * - DateTime picker for notification scheduling
 * - Loading state during save operation
 *
 * @param props - Component props containing dialog state and callbacks
 * @returns JSX element containing the save confirmation dialog
 */
export function SaveConfirmDialog({
  open,
  createOrder,
  notifications,
  notificationDateTime,
  isLoading,
  setNotifications,
  hasOkkoChanges,
  onClose,
  onConfirm,
  onCreateOrderChange,
  onNotificationChange,
  setNotificationDateTime
}: SaveConfirmDialogProps): JSX.Element {
  const { sendSMS, sendMail, send1C, sendOkkoCore } = notifications;
  const hasActiveNotifications = sendSMS || sendMail;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{ className: 'price-processing-dialog-paper' }}
      className="price-processing-confirm-dialog"
    >
      <DialogTitle>Підтвердження збереження цін</DialogTitle>
      <DialogContent className="price-processing-dialog-content">
        <>
          {hasOkkoChanges() && (
            <Box className="price-processing-notification-box">
              <FormControlLabel
                control={<Checkbox checked={createOrder} onChange={onCreateOrderChange} />}
                label="Сформувати розпорядження"
                className="price-processing-form-label"
              />
            </Box>
          )}

          {hasOkkoChanges() && createOrder && (
            <>
              <Box className="price-processing-notification-row">
                <Box className="price-processing-notification-column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendSMS}
                        onChange={e => {
                          setNotifications({ ...notifications, sendSMS: e.target.checked });
                          onNotificationChange(e.target.checked, sendMail);
                        }}
                      />
                    }
                    label="SMS"
                    className="price-processing-form-label-small"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendMail}
                        onChange={e => {
                          setNotifications({ ...notifications, sendMail: e.target.checked });
                          onNotificationChange(sendSMS, e.target.checked);
                        }}
                      />
                    }
                    label="MAIL"
                    className="price-processing-form-label-small"
                  />
                </Box>
                <Box className="price-processing-notification-column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={send1C}
                        onChange={e => setNotifications({ ...notifications, send1C: e.target.checked })}
                      />
                    }
                    label="1C Back Office"
                    className="price-processing-form-label-small"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendOkkoCore}
                        onChange={e => setNotifications({ ...notifications, sendOkkoCore: e.target.checked })}
                      />
                    }
                    label="OKKO Core (каса)"
                    className="price-processing-form-label-small"
                  />
                </Box>
              </Box>

              <Box className="price-processing-time-row">
                <Typography className="price-processing-time-label">Час сповіщення:</Typography>
                <Box className="price-processing-time-inputs">
                  <CustomDatePicker
                    value={notificationDateTime ? new Date(notificationDateTime) : null}
                    onChange={value => setNotificationDateTime(value as string)}
                    format="dd.MM.yyyy HH:mm"
                    showTime={true}
                    timeFormat="HH:mm"
                    includeSeconds={false}
                    outputFormat="datetime-local"
                    disabled={!hasActiveNotifications}
                  />
                </Box>
              </Box>
            </>
          )}
        </>
      </DialogContent>
      <DialogActions className="price-processing-dialog-actions">
        <Button onClick={onClose}>Скасувати</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {hasOkkoChanges() ? 'Провести ціни' : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
